import { createAction } from "redux-actions";
import { search, searchCategory } from "../../../lib/api/search";
import {
  REQUEST_SEARCH,
  RECEIVE_SEARCH,
  UPDATE_MULTI_FACET,
  CLEAR_SELECTIONS,
  MORE_PRODUCTS_LOADED,
  PREVIOUS_PRODUCTS_LOADED,
  LOADING_MORE_PRODUCTS,
  LOADING_PREVIOUS_PRODUCTS,
  UPDATE_SORT_FACET,
  SET_NEW_SEARCH,
  CLEAR_NEW_SEARCH
} from "./constants";
import logger from "../../../lib/logger";
import { getPaginationData } from "./selectors";
import { isLargeMediumViewPort } from "./viewportHelper";
import { getLocation } from "../router/selectors";
import {
  getSearchApiDefaults,
  getNorthAmericanRegion
} from "../config/selectors";
import config from "../../../config/common";
import applyFacets from "./facets/apply";
import { updateLocationQuery } from "../router/actions";
import { getMvtParams } from "../featureToggles/selectors";
import {
  analyticsLoadMore,
  analyticsLoadPrevious,
  analyticsRefinementsToggled,
  analyticsAddPendingRefinementsToggled,
  analyticsAddPendingSortRefinement,
  applyAndRemovePendingEvents
} from "../analytics/actions";
import { clearError, setError } from "../error";
import { ERROR_ORIGIN } from "../../../components/ErrorAlert/constants";
import { CATEGORY_PAGE } from "../page/constants";
import { filterInvalidProducts } from "./product";
import clientSideRedirect from "./clientSideRedirect";

export { default as clear } from "./facets/clear";
export { default as clearAll } from "./facets/clearAll";
export { default as selectAll } from "./facets/selectAll";
export { default as applyFacets } from "./facets/apply";
export { default as done } from "./facets/done";
export { undoSearch } from "./undo";
export {
  updateRangeFacet,
  setNextPriceRangeToCurrent,
  changeRangeFacet,
  applyRangeFacet,
  clearRangeFacet
} from "./range/actions";
const pageSize = config.api.product.search.pageSize;
export const requestProducts = createAction(REQUEST_SEARCH);
export const receiveProducts = createAction(RECEIVE_SEARCH, response => ({
  ...response,
  products: filterInvalidProducts(response.products)
}));
export const moreProductsLoaded = createAction(
  MORE_PRODUCTS_LOADED,
  response => ({
    ...response,
    products: filterInvalidProducts(response.products)
  })
);
export const previousProductsLoaded = createAction(
  PREVIOUS_PRODUCTS_LOADED,
  response => ({
    ...response,
    products: filterInvalidProducts(response.products)
  })
);
export const loadingPreviousProducts = createAction(LOADING_PREVIOUS_PRODUCTS);
export const loadingMoreProducts = createAction(LOADING_MORE_PRODUCTS);

export const getSearchAction = (state, payload) => {
  const {
    page: { name: pageName }
  } = state;
  const isCategory = pageName === CATEGORY_PAGE;
  const actionCreator = isCategory ? searchCategory : search;

  const newPayload = {
    ...payload,
    ...getMvtParams(state)
  };

  const northAmericanRegion = getNorthAmericanRegion(state);

  if (northAmericanRegion) {
    newPayload.region = northAmericanRegion;
  }

  return actionCreator(newPayload);
};

const getFetchAction = ({ page: { name: pageName } }) =>
  pageName === CATEGORY_PAGE ? fetchCategory() : fetchSearch();
export function retrySearch(actionType) {
  return (dispatch, getState) => {
    switch (actionType) {
      case PREVIOUS_PRODUCTS_LOADED:
        dispatch(loadPrevious());
        break;
      case MORE_PRODUCTS_LOADED:
        dispatch(loadMore());
        break;
      default:
        dispatch(getFetchAction(getState()));
        break;
    }
  };
}
export function loadPrevious() {
  return (dispatch, getState) => {
    const state = getState();
    const location = getLocation(state);
    const pagination = getPaginationData(state);
    const page = pagination.page.previous;
    if (pagination.offset.previous !== null) {
      dispatch(loadingPreviousProducts());

      return getSearchAction(state, {
        ...location.query,
        ...getSearchApiDefaults(state),
        offset: pagination.offset.previous
      })
        .then(response => {
          dispatch(clearError());
          dispatch(previousProductsLoaded(response));
          dispatch(analyticsLoadPrevious({ response, page }));
        })
        .catch(error => {
          logger.error(
            `${error.name} loadPrevious for ${location.query.q} failed, and caught error:`,
            error
          );
          dispatch(
            setError({
              message: error.message,
              actionType: PREVIOUS_PRODUCTS_LOADED,
              errorOrigin: ERROR_ORIGIN.search
            })
          );
          dispatch(previousProductsLoaded(error));
        });
    }
  };
}
export function loadMore() {
  return (dispatch, getState) => {
    const state = getState();
    const { query } = getLocation(state);
    const pagination = getPaginationData(state);
    const page = pagination.page.next;
    if (pagination.offset.next) {
      const updatedQuery = {
        ...query,
        page
      };
      dispatch(loadingMoreProducts());
      dispatch(updateLocationQuery({ query: updatedQuery }));

      return getSearchAction(state, {
        ...query,
        ...getSearchApiDefaults(state),
        offset: pagination.offset.next
      })
        .then(response => {
          dispatch(clearError());
          dispatch(moreProductsLoaded(response));
          dispatch(analyticsLoadMore({ response, page }));
        })
        .catch(error => {
          logger.error(
            `${error.name} loadMore for ${query.q} failed, and caught error:`,
            error
          );
          dispatch(
            setError({
              message: error.message,
              actionType: MORE_PRODUCTS_LOADED,
              errorOrigin: ERROR_ORIGIN.search
            })
          );
          dispatch(moreProductsLoaded(error));
        });
    }
  };
}

const getQueryObject = ({ state }) => {
  const { query } = getLocation(state);
  const queryObject = {
    ...query,
    ...getSearchApiDefaults(state),
    ...getMvtParams(state),

    offset:
      query.page && query.page > 0
        ? pageSize * (parseInt(query.page, 10) - 1)
        : 0
  };

  const northAmericanRegion = getNorthAmericanRegion(state);

  if (northAmericanRegion) {
    queryObject.region = northAmericanRegion;
  }

  return queryObject;
};

function fetchProduct(
  isCategory,
  uuid,
  xSiteOriginFromServerRequest,
  isNewPage
) {
  return (dispatch, getState) => {
    const state = getState();
    const queryObject = getQueryObject({ state });

    dispatch(requestProducts({ query: queryObject, isNewPage }));
    const searchFunction = isCategory ? searchCategory : search;

    return searchFunction(queryObject, uuid, xSiteOriginFromServerRequest)
      .then(response => {
        if (isNewPage) {
          clientSideRedirect(response);
        }
        dispatch(clearError());

        return dispatch(
          receiveProducts({
            ...response,
            query: queryObject,
            isNewPage
          })
        );
      })
      .then(() => {
        dispatch(applyAndRemovePendingEvents());
      })
      .catch(error => {
        if (isNewPage) {
          dispatch(receiveProducts(error));
          throw error;
        }
        logger.error(
          isCategory
            ? `${error.name} fetchCategory failed, and caught error:`
            : `${error.name} fetchSearch for ${queryObject.q} failed, and caught error:`,
          error
        );
        dispatch(
          setError({
            message: error.message,
            actionType: RECEIVE_SEARCH,
            errorOrigin: ERROR_ORIGIN.search
          })
        );

        return dispatch(receiveProducts(error));
      });
  };
}
export const fetchSearch = fetchProduct.bind(null, false);
export const fetchCategory = fetchProduct.bind(null, true);
export const updateMultiFacet = createAction(UPDATE_MULTI_FACET);
export function changeMultiFacet(toggledFacets) {
  return dispatch => {
    dispatch(updateMultiFacet(toggledFacets));
  };
}
export function changeMultiFacetAndApplyIfLargeMedium(toggledFacets) {
  return (dispatch, getState) => {
    dispatch(
      updateMultiFacet(
        toggledFacets.map(({ facet, facetValue, isSelected }) => ({
          facetId: facet.id,
          valueId: facetValue.id,
          isSelected
        }))
      )
    );
    if (isLargeMediumViewPort(getState())) {
      dispatch(applyFacets());
      dispatch(analyticsAddPendingRefinementsToggled(toggledFacets));
    } else {
      dispatch(analyticsRefinementsToggled(toggledFacets));
    }
  };
}
const updateSortFacet = createAction(UPDATE_SORT_FACET);
export const changeSort = ({ type, name }) => dispatch => {
  dispatch(updateSortFacet(type));
  dispatch(analyticsAddPendingSortRefinement({ type, name }));
  dispatch(applyFacets());
};
export const clearSelections = createAction(CLEAR_SELECTIONS);
export const setNewSearch = createAction(SET_NEW_SEARCH);
export const clearNewSearch = createAction(CLEAR_NEW_SEARCH);
